import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { formatFileSize } from '../compress/compressPdfEngine';
import PdfDropZone from '../../components/pdf/PdfDropZone';
import { PdfUploadedFile } from '../../components/pdf/PdfFileRow';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import PdfDocPreview from '../../components/pdf/PdfDocPreview';
import ToolReadyHint from '../../components/common/ToolReadyHint';
import PdfEditorModal from '../../../../../features/pdf-editor/PdfEditorModal';
import {
    deriveToolStatus,
    downloadBlob,
    genericHowToSteps,
    getToolStatusText,
} from '../../toolPageUtils';
import { printBlob } from '../../../../../utils/generateDocPdf';
import { usePdfPaste } from '../../components/common/useFilePaste';

const MAX_FILE_BYTES = 300 * 1024 * 1024;

/**
 * Landing page for the full-screen PDF editor. Arriving here opens the editor
 * straight away with an upload card in its middle; this page is what is left
 * behind when the editor is closed or minimised. Every edit happens inside
 * `PdfEditorModal`, entirely in the browser.
 *
 * Once there is a file the page drops its file column and the preview takes
 * the full width — the editor is where the file is managed.
 */
export default function EditPdf() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);

    const [file, setFile] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [editorOpen, setEditorOpen] = useState(true);
    // Minimised: the editor stays mounted (document and edits intact) but
    // hidden, and this page shows until it is restored.
    const [minimized, setMinimized] = useState(false);
    // The PDF as it stood when the editor was last minimised ({ blob, name }),
    // so the preview behind it shows the edits — and can download them.
    const [result, setResult] = useState(null);
    const [previewBuilding, setPreviewBuilding] = useState(false);
    const buildSeq = useRef(0);
    const [error, setError] = useState(null);

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        if (!list.length) return;

        const next = list[0];
        if (!(next.type === 'application/pdf' || next.name.toLowerCase().endsWith('.pdf'))) {
            const msg = t('editPdf.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }
        if (next.size > MAX_FILE_BYTES) {
            const msg = t('editPdf.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        buildSeq.current += 1;
        setPreviewBuilding(false);
        setFile(next);
        setResult(null);
        setError(null);
        setEditorOpen(true);
        setMinimized(false);
        recordToolUsage('pdf/edit', { category: 'pdf' });
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied PDF straight in.
    // Only while waiting for a file: once one is open in the editor, a paste
    // would swap it for another and throw every edit away.
    usePdfPaste(addFiles, { enabled: !file });

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    const clearAll = () => {
        buildSeq.current += 1;
        setPreviewBuilding(false);
        setFile(null);
        setResult(null);
        setError(null);
        setEditorOpen(false);
        setMinimized(false);
    };

    // Minimised: show the page, and swap the preview to the edited PDF once the
    // editor has built it. A later minimise supersedes a build still running.
    const handleMinimize = (edited) => {
        setMinimized(true);
        if (!edited) return;
        const seq = ++buildSeq.current;
        setPreviewBuilding(true);
        edited
            .then((blob) => {
                if (seq !== buildSeq.current) return;
                setResult({ blob, name: file?.name || 'document.pdf' });
            })
            .catch(() => {
                if (seq !== buildSeq.current) return;
                notifications.show({ title: t('common.error'), message: t('pdfEditor.errors.export'), color: 'red' });
            })
            .finally(() => {
                if (seq === buildSeq.current) setPreviewBuilding(false);
            });
    };

    // Bring a minimised editor back as it was; otherwise open it fresh.
    const openEditor = () => {
        if (minimized) setMinimized(false);
        else if (file) setEditorOpen(true);
    };

    const status = deriveToolStatus({
        hasInput: !!file,
        canRun: !!file,
        processing: previewBuilding,
        hasResult: !!result,
        hasError: !!error,
    });

    const howToSteps = useMemo(() => [1, 2, 3, 4].map((i) => t(`editPdf.steps.step${i}`, {
        defaultValue: genericHowToSteps(t)[i - 1],
    })), [t]);

    const statusText = getToolStatusText(status, t, {
        ready: t('editPdf.statusReady'),
        success: t('editPdf.done'),
        error: error || t('toolPage.statusText.error'),
    });

    return (
        <>
            <ToolPageLayout
                slug="pdf/edit"
                subtitleKey="editPdf.subtitle"
                status={status}
                statusText={statusText}
                controlsTitle={t('toolPage.files', { count: toBnDigits(file ? 1 : 0) })}
                showAdd={!file}
                onAdd={() => fileInputRef.current?.click()}
                howToSteps={howToSteps}
                onReset={file || result || error ? clearAll : undefined}
                hideControls={!!file}
                primaryAction={{
                    label: result ? t('editPdf.reopen') : t('editPdf.open'),
                    onClick: openEditor,
                    disabled: !file,
                }}
                controls={(
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="application/pdf,.pdf"
                            className="hidden"
                            onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                        />

                        {!file ? (
                            <PdfDropZone
                                title={t('editPdf.dropTitle')}
                                hint={t('editPdf.dropHint')}
                                isDragging={isDragging}
                                onClick={() => fileInputRef.current?.click()}
                                onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
                                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                                onDragLeave={() => setIsDragging(false)}
                                onDrop={handleDrop}
                            />
                        ) : (
                            <PdfUploadedFile
                                name={file.name}
                                meta={formatFileSize(file.size)}
                                onRemove={clearAll}
                                removeLabel={t('editPdf.removeFile')}
                            />
                        )}
                    </>
                )}
                settings={null}
                preview={(
                    <ToolPreviewPanel
                        title={t('toolPage.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('toolPage.previewEmpty')}
                        idleHint={t('toolPage.previewEmptyHint')}
                        error={error}
                        successLabel={result ? formatFileSize(result.blob.size) : t('editPdf.done')}
                        downloadLabel={t('editPdf.download')}
                        onDownload={result ? () => downloadBlob(result.blob, result.name) : undefined}
                        onPrint={result ? () => printBlob(result.blob) : undefined}
                        onEditAgain={result ? openEditor : undefined}
                        editAgainLabel={t('editPdf.reopen')}
                        onAgain={result ? clearAll : undefined}
                        againLabel={t('editPdf.startOver')}
                        readyHint={status === 'ready' ? <ToolReadyHint actionLabel={t('editPdf.open')} /> : null}
                        onIdleClick={() => fileInputRef.current?.click()}
                        onIdleDrop={handleDrop}
                        onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                        onIdleDragLeave={() => setIsDragging(false)}
                        idleDragging={isDragging}
                        showIdle={!file && !error}
                        pdfPreview={!!file && !error}
                    >
                        {file && !error ? (
                            <PdfDocPreview source={result?.blob || file} />
                        ) : null}
                    </ToolPreviewPanel>
                )}
            />

            <PdfEditorModal
                open={editorOpen}
                minimized={minimized}
                // Always the ORIGINAL file: the edited copy is a flattened
                // export, and reopening the editor on it would lose every
                // editable object (the minimised editor still holds them).
                file={file}
                fileName={file?.name}
                clickToEditText
                onPickFile={addFiles}
                onMinimize={handleMinimize}
                // Closing the editor leaves the tool altogether, back to the PDF
                // tools list; minimise is the way to this page. (The editor has
                // already asked before throwing away unsaved edits.)
                onClose={() => navigate('/tools?tab=pdf')}
            />
        </>
    );
}
